import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const verifyPayment = async () => {
      const reference = searchParams.get('reference') || searchParams.get('trxref');
      
      if (!reference) {
        setIsVerifying(false);
        setIsSuccess(false);
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke('paystack-verify', {
          body: { reference },
        });

        if (error) throw error;

        if (data?.verified) {
          setIsSuccess(true);
          toast({
            title: 'Payment Successful!',
            description: 'Your plan has been upgraded successfully.',
          });
          
          // Redirect to billing after 3 seconds
          setTimeout(() => {
            navigate('/billing-new');
          }, 3000);
        } else {
          setIsSuccess(false);
          toast({
            title: 'Payment Verification Failed',
            description: 'Unable to verify your payment. Please contact support.',
            variant: 'destructive',
          });
        }
      } catch (error: any) {
        console.error('Verification error:', error);
        setIsSuccess(false);
        toast({
          title: 'Error',
          description: error.message || 'Failed to verify payment',
          variant: 'destructive',
        });
      } finally {
        setIsVerifying(false);
      }
    };

    verifyPayment();
  }, [searchParams, navigate, toast]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <Helmet>
        <title>Payment Success | Bravax Forms</title>
      </Helmet>
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center">
          {isVerifying ? (
            <>
              <Loader2 className="h-16 w-16 animate-spin text-primary mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">Verifying Payment</h2>
              <p className="text-muted-foreground">
                Please wait while we confirm your payment...
              </p>
            </>
          ) : isSuccess ? (
            <>
              <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">Payment Successful!</h2>
              <p className="text-muted-foreground mb-6">
                Your plan has been upgraded successfully. Redirecting to billing page...
              </p>
              <Button onClick={() => navigate('/billing-new')} className="w-full">
                Go to Billing
              </Button>
            </>
          ) : (
            <>
              <XCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">Payment Failed</h2>
              <p className="text-muted-foreground mb-6">
                Unable to verify your payment. Please contact support if money was deducted.
              </p>
              <div className="space-y-3">
                <Button onClick={() => navigate('/billing-new')} className="w-full">
                  Back to Billing
                </Button>
                <Button onClick={() => navigate('/settings')} variant="outline" className="w-full">
                  Contact Support
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
